import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Radio,
  RadioGroup,
  Button,
  Grid,
  Paper,
  Divider,
  Chip,
  CircularProgress,
} from "@mui/material";
import CalculateIcon from "@mui/icons-material/Calculate";
import SaveIcon from "@mui/icons-material/Save";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import FunctionsIcon from "@mui/icons-material/Functions";
import { toast } from "react-toastify";
import OrgNavbar from "../../components/Org/OrgNavbar";
import { BASE_URL } from "../../constants";

// The calculation question definitions with simple 0, 1 options
const CALCULATION_QUESTIONS = [
  {
    id: "discount2Percent",
    num: 1,
    title: "How Discount 2 (%) is calculated ?",
    description:
      "Defines secondary line item percentage discount calculation and its interaction with Discount 1 and Tax.",
    options: [
      {
        value: 0,
        label: "Qty X Rate X Discount 2 %",
        formula: "Qty × Rate × (Discount2 / 100)",
        hint: "Direct gross value discount independent of Discount 1",
      },
      {
        value: 1,
        label: "Qty X Rate X (100-Discount 1)% X Discount 2%",
        formula: "Qty × Rate × (1 - Discount1%) × (Discount2 / 100)",
        hint: "Compounded discount: calculated on net amount after Discount 1",
      },
    ],
    default: 0,
  },
  {
    id: "discount1Rs",
    num: 2,
    title: "How Discount 1 (Rs) is calculated ?",
    description:
      "Defines how fixed rupee amount Discount 1 is applied across quantity or bill amount.",
    options: [
      {
        value: 0,
        label: "Qty X Discount1",
        formula: "Quantity × Fixed Discount1 (Rs)",
        hint: "Per-unit flat rate discount (e.g. ₹5 per unit)",
      },
      {
        value: 1,
        label: "Before Tax on Amount",
        formula: "Gross Amount (Pre-Tax) - Discount1 (Rs)",
        hint: "Deducted directly from pre-tax gross total",
      },
    ],
    default: 0,
  },
  {
    id: "discount2Rs",
    num: 3,
    title: "How Discount 2 (Rs) is calculated ?",
    description:
      "Defines how fixed rupee amount Discount 2 is applied across quantity or bill amount.",
    options: [
      {
        value: 0,
        label: "Qty X Discount2",
        formula: "Quantity × Fixed Discount2 (Rs)",
        hint: "Per-unit flat rate discount (e.g. ₹5 per unit)",
      },
      {
        value: 1,
        label: "Before Tax on Amount",
        formula: "Gross Amount (Pre-Tax) - D2 (Rs)",
        hint: "Deducted directly from pre-tax gross total",
      },
    ],
    default: 0,
  },
  {
    id: "billDiscount2Percent",
    num: 4,
    title: "How Bill Discount 2 (%) is calculated ?",
    description:
      "Defines secondary bill-level discount percentage and its cascade effect with Bill Discount 1.",
    options: [
      {
        value: 0,
        label: "Net Bill Amount X BD 2 (%)",
        formula: "Net Bill Amount × (BD2 / 100)",
        hint: "Calculated on initial net bill amount independently",
      },
      {
        value: 1,
        label: "{Net Bill Amount - (100-BD 1)%} X BD 2 %",
        formula: "[Net Bill Amount - BD1 Amount] × (BD2 / 100)",
        hint: "Compounded: calculated on balance after deducting Bill Discount 1",
      },
    ],
    default: 0,
  },

  // {
  //   id: "billDiscount1Tax",
  //   num: 5,
  //   title: "Bill Discount 1 affects tax?",
  //   description: "",
  //   options: [
  //     {
  //       value: 0,
  //       label: "No",
  //       formula: "",
  //       hint: "",
  //     },
  //     {
  //       value: 1,
  //       label: "Yes",
  //       formula: "",
  //       hint: "",
  //     },
  //   ],
  //   default: 0,
  // },

  // {
  //   id: "billDiscount2Tax",
  //   num: 5,
  //   title: "Bill Discount 2 affects tax?",
  //   description: "",
  //   options: [
  //     {
  //       value: 0,
  //       label: "No",
  //       formula: "",
  //       hint: "",
  //     },
  //     {
  //       value: 1,
  //       label: "Yes",
  //       formula: "",
  //       hint: "",
  //     },
  //   ],
  //   default: 0,
  // },
];

const DEFAULT_CONFIG = CALCULATION_QUESTIONS.reduce((acc, q) => {
  acc[q.id] = q.default;
  return acc;
}, {});

const Calculation = () => {
  const [configs, setConfigs] = useState(DEFAULT_CONFIG);
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);

  const orgId = localStorage.getItem("orgId") || "default";
  const storageKey = `org_calculation_settings_${orgId}`;

  // Load saved configuration from localStorage or backend
  useEffect(() => {
    try {
      const savedLocal = localStorage.getItem(storageKey);
      if (savedLocal) {
        const parsed = JSON.parse(savedLocal);
        setConfigs((prev) => ({ ...prev, ...parsed }));
      }
    } catch (e) {
      console.warn("Failed to load local calculation settings", e);
    }

    // Attempt to fetch from backend
    const fetchRemoteSettings = async () => {
      try {
        const response = await fetch(
          `${BASE_URL}api/org/calculation-settings`,
          {
            method: "GET",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
          },
        );
        if (response.ok) {
          const resData = await response.json();
          if (resData.success && resData.data) {
            setConfigs((prev) => ({ ...prev, ...resData.data }));
            localStorage.setItem(storageKey, JSON.stringify(resData.data));
          }
        }
      } catch (err) {
        // Fallback silently to local settings
      }
    };

    fetchRemoteSettings();
  }, [storageKey]);

  const handleChange = (questionId, value) => {
    setConfigs((prev) => ({
      ...prev,
      [questionId]: Number(value),
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // 1. Always persist to localStorage
      localStorage.setItem(storageKey, JSON.stringify(configs));
      localStorage.setItem("org_calculation_settings", JSON.stringify(configs));

      // 2. Attempt backend persistence
      try {
        await fetch(`${BASE_URL}api/org/calculation-settings`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            orgId,
            settings: configs,
          }),
        });
      } catch (backendErr) {
        // Local persistence succeeded, backend endpoint may be optional/work in progress
        console.warn("Backend calculation save notice:", backendErr);
      }

      setLastSaved(new Date().toLocaleTimeString());
      toast.success("Calculation settings saved successfully!");
    } catch (error) {
      console.error("Save error:", error);
      toast.error("Failed to save calculation settings.");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setConfigs(DEFAULT_CONFIG);
    toast.info("Reset to default calculation settings. Click Save to persist.");
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f4f6fb", pb: 8 }}>
      {/* ── Organization Top Navbar ── */}
      <OrgNavbar />

      {/* ── Header Banner ── */}
      <Box
        sx={{
          background:
            "linear-gradient(135deg, #0a1f44 0%, #1e3a8a 50%, #4c1d95 100%)",
          color: "#ffffff",
          pt: 4,
          pb: 5,
          px: { xs: 2, sm: 4 },
          borderBottom: "1px solid rgba(255,255,255,0.1)",
          boxShadow: "0 8px 24px rgba(10,31,68,0.12)",
        }}
      >
        <Container maxWidth="lg">
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              justifyContent: "space-between",
              alignItems: { xs: "flex-start", md: "center" },
              gap: 2,
            }}
          >
            <Box>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}
              >
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: "12px",
                    bgcolor: "rgba(255,255,255,0.12)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    border: "1px solid rgba(255,255,255,0.2)",
                  }}
                >
                  <CalculateIcon
                    sx={{ fontSize: "1.7rem", color: "#c084fc" }}
                  />
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 800,
                    letterSpacing: "-0.5px",
                    fontSize: { xs: "1.5rem", sm: "1.9rem" },
                  }}
                >
                  Calculations Configuration
                </Typography>
              </Box>
              <Typography
                variant="body2"
                sx={{
                  color: "rgba(255,255,255,0.8)",
                  maxWidth: 680,
                  lineHeight: 1.5,
                  fontSize: "0.92rem",
                }}
              >
                Define the mathematical rules for line item discounts, tax
                interactions, rupee deductions, and bill-level discounts used
                across purchase and sales billing.
              </Typography>
            </Box>

            {/* Quick Actions */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                flexWrap: "wrap",
              }}
            >
              <Button
                variant="outlined"
                onClick={handleReset}
                startIcon={<RestartAltIcon />}
                sx={{
                  color: "#ffffff",
                  borderColor: "rgba(255,255,255,0.3)",
                  borderRadius: "10px",
                  px: 2,
                  py: 1,
                  textTransform: "none",
                  fontWeight: 600,
                  "&:hover": {
                    borderColor: "#ffffff",
                    bgcolor: "rgba(255,255,255,0.1)",
                  },
                }}
              >
                Reset Defaults
              </Button>

              {/* <Button
                variant="contained"
                onClick={handleSave}
                disabled={saving}
                startIcon={
                  saving ? (
                    <CircularProgress size={18} sx={{ color: "#ffffff" }} />
                  ) : (
                    <SaveIcon />
                  )
                }
                sx={{
                  background:
                    "linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)",
                  boxShadow: "0 4px 14px rgba(168,85,247,0.4)",
                  borderRadius: "10px",
                  px: 3,
                  py: 1,
                  textTransform: "none",
                  fontWeight: 700,
                  "&:hover": {
                    background:
                      "linear-gradient(135deg, #6d28d9 0%, #9333ea 100%)",
                  },
                }}
              >
                {saving ? "Saving..." : "Save Settings"}
              </Button> */}
            </Box>
          </Box>

          {lastSaved && (
            <Box sx={{ mt: 2 }}>
              <Chip
                icon={
                  <CheckCircleOutlineIcon
                    sx={{ color: "#34d399 !important" }}
                  />
                }
                label={`Last saved at ${lastSaved}`}
                size="small"
                sx={{
                  bgcolor: "rgba(16,185,129,0.15)",
                  color: "#a7f3d0",
                  border: "1px solid rgba(16,185,129,0.3)",
                  fontWeight: 600,
                  fontSize: "0.75rem",
                }}
              />
            </Box>
          )}
        </Container>
      </Box>

      {/* ── Main Content Container ── */}
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Grid container spacing={3.5}>
          {CALCULATION_QUESTIONS.map((question) => {
            const selectedVal = configs[question.id] || question.default;
            return (
              <Grid item xs={12} key={question.id}>
                <Card
                  elevation={0}
                  sx={{
                    borderRadius: "16px",
                    border: "1px solid #e2e8f0",
                    background: "#ffffff",
                    boxShadow: "0 2px 10px rgba(15,23,42,0.04)",
                    overflow: "hidden",
                    transition: "box-shadow 0.2s ease, border-color 0.2s ease",
                    "&:hover": {
                      borderColor: "#cbd5e1",
                      boxShadow: "0 6px 20px rgba(15,23,42,0.07)",
                    },
                  }}
                >
                  <CardContent sx={{ p: { xs: 2.5, sm: 3.5 } }}>
                    {/* Question Header */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 2,
                        mb: 2,
                      }}
                    >
                      <Box
                        sx={{
                          width: 38,
                          height: 38,
                          borderRadius: "10px",
                          background:
                            "linear-gradient(135deg, #2a54a6 0%, #6D28D9 100%)",
                          color: "#ffffff",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontWeight: 800,
                          fontSize: "0.95rem",
                          flexShrink: 0,
                          boxShadow: "0 3px 8px rgba(109,40,217,0.25)",
                        }}
                      >
                        {question.num}
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 700,
                            color: "#0f172a",
                            fontSize: { xs: "1rem", sm: "1.12rem" },
                            lineHeight: 1.3,
                          }}
                        >
                          {question.title}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: "#64748b",
                            mt: 0.5,
                            fontSize: "0.82rem",
                          }}
                        >
                          {question.description}
                        </Typography>
                      </Box>
                    </Box>

                    <Divider sx={{ my: 2, borderColor: "#f1f5f9" }} />

                    {/* Radio Options Grid */}
                    <RadioGroup
                      value={selectedVal}
                      onChange={(e) =>
                        handleChange(question.id, Number(e.target.value))
                      }
                    >
                      <Grid container spacing={1.5}>
                        {question.options.map((opt, idx) => {
                          const isChecked =
                            Number(selectedVal) === Number(opt.value);
                          return (
                            <Grid
                              item
                              xs={12}
                              md={question.options.length > 2 ? 6 : 6}
                              key={opt.value}
                            >
                              <Paper
                                onClick={() =>
                                  handleChange(question.id, opt.value)
                                }
                                elevation={0}
                                sx={{
                                  p: 2,
                                  borderRadius: "12px",
                                  border: isChecked
                                    ? "2px solid #7c3aed"
                                    : "1px solid #e2e8f0",
                                  bgcolor: isChecked
                                    ? "rgba(124,58,237,0.04)"
                                    : "#ffffff",
                                  cursor: "pointer",
                                  transition: "all 0.18s ease",
                                  height: "100%",
                                  display: "flex",
                                  flexDirection: "column",
                                  justifyContent: "space-between",
                                  "&:hover": {
                                    borderColor: isChecked
                                      ? "#7c3aed"
                                      : "#94a3b8",
                                    bgcolor: isChecked
                                      ? "rgba(124,58,237,0.07)"
                                      : "#f8fafc",
                                    transform: "translateY(-1px)",
                                  },
                                }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "flex-start",
                                    gap: 1.5,
                                  }}
                                >
                                  <Radio
                                    checked={isChecked}
                                    value={opt.value}
                                    sx={{
                                      p: 0.5,
                                      color: "#94a3b8",
                                      "&.Mui-checked": {
                                        color: "#7c3aed",
                                      },
                                    }}
                                  />
                                  <Box sx={{ flex: 1 }}>
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        gap: 1,
                                      }}
                                    >
                                      <Typography
                                        sx={{
                                          fontWeight: isChecked ? 700 : 600,
                                          fontSize: "0.92rem",
                                          color: isChecked
                                            ? "#4c1d95"
                                            : "#1e293b",
                                        }}
                                      >
                                        <span
                                          style={{
                                            color: "#7c3aed",
                                            marginRight: 6,
                                          }}
                                        >
                                          {idx + 1}.
                                        </span>
                                        {opt.label}
                                      </Typography>
                                      {isChecked && (
                                        <Chip
                                          label="Selected"
                                          size="small"
                                          sx={{
                                            bgcolor: "#7c3aed",
                                            color: "#ffffff",
                                            fontWeight: 700,
                                            fontSize: "0.68rem",
                                            height: 20,
                                          }}
                                        />
                                      )}
                                    </Box>

                                    {/* Formula representation */}
                                    <Box
                                      sx={{
                                        mt: 1,
                                        display: "inline-flex",
                                        alignItems: "center",
                                        gap: 0.75,
                                        px: 1.25,
                                        py: 0.5,
                                        borderRadius: "6px",
                                        bgcolor: isChecked
                                          ? "#ede9fe"
                                          : "#f1f5f9",
                                        border: isChecked
                                          ? "1px solid #ddd6fe"
                                          : "1px solid #e2e8f0",
                                      }}
                                    >
                                      <FunctionsIcon
                                        sx={{
                                          fontSize: "0.85rem",
                                          color: isChecked
                                            ? "#7c3aed"
                                            : "#64748b",
                                        }}
                                      />
                                      <Typography
                                        sx={{
                                          fontFamily: "monospace",
                                          fontSize: "0.78rem",
                                          fontWeight: 600,
                                          color: isChecked
                                            ? "#5b21b6"
                                            : "#475569",
                                        }}
                                      >
                                        {opt.formula}
                                      </Typography>
                                    </Box>

                                    {/* Hint */}
                                    <Typography
                                      sx={{
                                        mt: 1,
                                        fontSize: "0.75rem",
                                        color: "#64748b",
                                        lineHeight: 1.4,
                                      }}
                                    >
                                      {opt.hint}
                                    </Typography>
                                  </Box>
                                </Box>
                              </Paper>
                            </Grid>
                          );
                        })}
                      </Grid>
                    </RadioGroup>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>

        {/* ── Bottom Save Action Bar ── */}
        <Paper
          elevation={4}
          sx={{
            position: "sticky",
            bottom: 24,
            mt: 4,
            p: 2.5,
            borderRadius: "16px",
            background: "rgba(255,255,255,0.95)",
            backdropFilter: "blur(12px)",
            border: "1px solid #cbd5e1",
            boxShadow: "0 10px 30px rgba(15,23,42,0.12)",
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            zIndex: 10,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: "9px",
                bgcolor: "rgba(124,58,237,0.1)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <SaveIcon sx={{ color: "#7c3aed", fontSize: "1.2rem" }} />
            </Box>
            <Box>
              <Typography
                sx={{ fontWeight: 700, fontSize: "0.92rem", color: "#0f172a" }}
              >
                Ready to save your calculation rules?
              </Typography>
              <Typography sx={{ fontSize: "0.75rem", color: "#64748b" }}>
                Settings take effect immediately for future purchase and sales
                transactions.
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Button
              variant="outlined"
              onClick={handleReset}
              sx={{
                borderRadius: "10px",
                px: 2.5,
                py: 0.9,
                textTransform: "none",
                fontWeight: 600,
                color: "#64748b",
                borderColor: "#cbd5e1",
                "&:hover": {
                  borderColor: "#94a3b8",
                  bgcolor: "#f8fafc",
                },
              }}
            >
              Reset Defaults
            </Button>

            <Button
              variant="contained"
              onClick={handleSave}
              disabled={saving}
              startIcon={
                saving ? (
                  <CircularProgress size={18} sx={{ color: "#ffffff" }} />
                ) : (
                  <SaveIcon />
                )
              }
              sx={{
                background: "linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)",
                boxShadow: "0 4px 14px rgba(168,85,247,0.4)",
                borderRadius: "10px",
                px: 3.5,
                py: 0.9,
                textTransform: "none",
                fontWeight: 700,
                "&:hover": {
                  background:
                    "linear-gradient(135deg, #6d28d9 0%, #9333ea 100%)",
                },
              }}
            >
              {saving ? "Saving..." : "Save Calculations"}
            </Button>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default Calculation;
